/**
 * Bench summary numbers for `ListPage stats` (NextAdmin overview-stats; demo /manage-team header numbers).
 */
import { AlertTriangle, Briefcase, CheckCircle2, UserMinus, Users } from 'lucide-react';
import type { StatStripCell } from '../../../components/ui/index.js';
import type { DaemonLoadBuckets } from '../../../generated/api/types.gen.js';
import type { FleetSummary } from '../shared';

function join(parts: Array<[number, string]>, empty: string): string {
  const nonzero = parts.filter(([n]) => n > 0).map(([n, label]) => `${n} ${label}`);
  return nonzero.length ? nonzero.join(' · ') : empty;
}

export function benchStatCells(summary: FleetSummary, loadBuckets?: DaemonLoadBuckets): StatStripCell[] {
  const cells: StatStripCell[] = [
    {
      label: 'Total owned',
      value: summary.totalOwned,
      sub: join(
        [
          [summary.aiCount, 'AI'],
          [summary.humanCount, 'human'],
        ],
        'No employees',
      ),
      icon: Users,
    },
    {
      label: 'Active',
      value: summary.byLifecycle.active,
      sub: join(
        [
          [summary.byLifecycle.paused, 'paused'],
          [summary.byLifecycle.needs_setup, 'in setup'],
          [summary.byLifecycle.terminated, 'terminated'],
        ],
        'All active',
      ),
      severity: 'success',
      icon: CheckCircle2,
    },
    {
      label: 'Assigned',
      value: summary.assignedEmployees,
      sub: `${summary.activeAssignments} active ${summary.activeAssignments === 1 ? 'assignment' : 'assignments'}`,
      severity: 'accent',
      icon: Briefcase,
    },
    {
      label: 'Unassigned',
      value: summary.unassignedEmployees,
      sub: 'Active, without a project',
      severity: summary.unassignedEmployees > 0 ? 'warning' : undefined,
      icon: UserMinus,
    },
    {
      label: 'Needs attention',
      value: summary.needsAttention,
      sub: join(
        [
          [summary.attentionBreakdown.blocked, 'blocked'],
          [summary.attentionBreakdown.needs_review, 'in review'],
          [summary.attentionBreakdown.permission_needed, 'need a permission'],
        ],
        'All clear',
      ),
      severity: summary.needsAttention > 0 ? 'danger' : undefined,
      icon: AlertTriangle,
    },
  ];
  if (loadBuckets) {
    cells.push(
      {
        label: 'At capacity',
        value: loadBuckets.at_capacity,
        sub: 'Daemons at their assignment limit',
        severity: loadBuckets.at_capacity > 0 ? 'warning' : undefined,
      },
      {
        label: 'Over capacity',
        value: loadBuckets.over_capacity,
        sub: 'Daemons over their limit',
        severity: loadBuckets.over_capacity > 0 ? 'danger' : undefined,
      },
    );
  }
  return cells;
}
